import { DIST_EPSILON } from "geom/core";
import type { Vec3 } from "geom/vec3";
import { vec3Add, vec3Cross, vec3Dot, vec3Len, vec3LenSquared, vec3Scale, vec3Sub } from "geom/vec3";

// The distance between a line and a point.
// (ax, ay, az) is a position on the line.
// (dx, dy, dz) is the direction of the line.
// (bx, by, bz) is the point we are measuring the distance of.
export function linePointDistance(
  ax: number,
  ay: number,
  az: number,
  dx: number,
  dy: number,
  dz: number,
  bx: number,
  by: number,
  bz: number,
): number {
  const [abx, aby, abz] = vec3Sub(bx, by, bz, ax, ay, az);
  const [cx, cy, cz] = vec3Cross(dx, dy, dz, abx, aby, abz);
  return vec3Len(cx, cy, cz);
}

// The intersection point is supposed to happen in front of each ray. This is allowed tolerance
// for the intersection point to go behind the start of the ray.
const INTERSECTION_TOLERANCE = 0.1;

const MIN_ANGLE = DIST_EPSILON;
const MIN_CROSS_PRODUCT = Math.sin(MIN_ANGLE);

// Intersect two 3D rays.
// One ray starts at (ax, ay, az) and is in the (ex, ey, ez) direction.
// The other starts at (bx, by, bz) and is in the (fx, fy, fz) direction.
// If they intersect, determine where they do.
// If forwardOnly is true, do not allow intersects in the backwards direction.
export function intersect3D(
  ax: number,
  ay: number,
  az: number,
  ex: number,
  ey: number,
  ez: number,
  bx: number,
  by: number,
  bz: number,
  fx: number,
  fy: number,
  fz: number,
  forwardOnly: boolean,
): Vec3 {
  const [gx, gy, gz] = vec3Sub(bx, by, bz, ax, ay, az);
  const [hx, hy, hz] = vec3Cross(fx, fy, fz, gx, gy, gz);
  const [kx, ky, kz] = vec3Cross(fx, fy, fz, ex, ey, ez);

  // Returns null if the two lines are parallel.
  if (vec3LenSquared(kx, ky, kz) < MIN_CROSS_PRODUCT) {
    return [NaN, NaN, NaN];
  }

  const sign = vec3Dot(hx, hy, hz, kx, ky, kz) > 0 ? 1 : -1;
  const hLen = vec3Len(hx, hy, hz);
  const kLen = vec3Len(kx, ky, kz);
  const [esx, esy, esz] = vec3Scale(ex, ey, ez, (sign * hLen) / kLen);
  const [px, py, pz] = vec3Add(ax, ay, az, esx, esy, esz);

  const [tx, ty, tz] = vec3Sub(px, py, pz, ax, ay, az);
  const [ux, uy, uz] = vec3Sub(px, py, pz, bx, by, bz);
  let t = vec3Dot(tx, ty, tz, ex, ey, ez);
  let u = vec3Dot(ux, uy, uz, fx, fy, fz);

  if (!forwardOnly) {
    t = Math.abs(t);
    u = Math.abs(u);
  }

  if (-INTERSECTION_TOLERANCE < t && -INTERSECTION_TOLERANCE < u) {
    return [px, py, pz];
  } else {
    return [NaN, NaN, NaN];
  }
}
